import { MeetingDetails } from '@eigtech/meetings-types'
import { Corn } from '@eigtech/shared-corn'
import { useUserContext } from '@eigtech/ui-shared-auth'
import {
  ACTION_MENU_ID,
  createColumnHelper,
  DataGrid,
  FilterFnOption,
  TableState,
} from '@eigtech/ui-shared-data-grid'
import { isISODate, STANDARD_DATE_TIME_FORMAT, useDatesContext } from '@eigtech/ui-shared-dates'
import {
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useCopyToClipboard,
  useDisclosure,
} from '@eigtech/ui-shared-dave'
import { FC, useMemo, useState } from 'react'
import { MdMoreHoriz } from 'react-icons/md'
import { useListMeetings } from '../../api/listMeetings'
import { UpdateMeetingFormDrawer } from '../Forms/UpdateMeetingFormDrawer'

export type MeetingDetailsWithoutChimeEvents = Omit<MeetingDetails, 'chimeEvents'>

export type MeetingsTableProps = {
  entityId: Corn
  ActionMenuItems?: FC<MeetingDetailsWithoutChimeEvents>
  ActionButtons?: FC<MeetingDetailsWithoutChimeEvents>
}

const startEndToDuration = (startTime: string, endTime?: string): string => {
  if (!endTime || !isISODate(startTime) || !isISODate(endTime)) return '(Unknown)'
  const msDur = new Date(endTime).valueOf() - new Date(startTime).valueOf()
  return `${Math.round(msDur / 1000 / 60)} minutes`
}

export function MeetingsTable({ entityId, ActionMenuItems, ActionButtons }: MeetingsTableProps) {
  const { data, isPending, isFetching, isError, refetch } = useListMeetings({ entityId })

  const [dateStartedFilterFn, setDateStartedFilterFn] =
    useState<FilterFnOption<AugmentedMeetingDetails>>('dateEquals')

  const { PreferredDateTimeComponent: DateComponent } = useDatesContext()
  const { PreferredUserComponent: UserComponent } = useUserContext()

  const meetings = useMemo(
    () =>
      (data ?? []).map(
        (meeting): AugmentedMeetingDetails => ({
          ...meeting,
          description: meeting.description ?? '',
          duration: startEndToDuration(meeting.startTime, meeting.endTime),
          numberOfParticipants: meeting.participants.length,
        })
      ),
    [data]
  )

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        id: ACTION_MENU_ID,
        header: '',
        enableSorting: false,
        enableColumnFilter: false,
        maxSize: 80,
        cell: (cell) => (
          <MeetingActionMenu
            {...cell.row.original}
            ActionButtons={ActionButtons}
            ActionMenuItems={ActionMenuItems}
          />
        ),
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: 'Description',
      }),
      columnHelper.accessor('startTime', {
        id: 'startTime',
        header: 'Started',
        filterFn: dateStartedFilterFn,
        meta: {
          availableFilterFns: [
            { label: 'Date Equals', value: 'dateEquals' },
            { label: 'Date Greater Than', value: 'dateGreaterThan' },
            { label: 'Date Lesser Than', value: 'dateLesserThan' },
          ],
          onFilterFnChange: setDateStartedFilterFn,
        },
        cell: (cell) => (
          <DateComponent
            {...cell.row.original}
            format={STANDARD_DATE_TIME_FORMAT}
            property="endTime"
          >
            {cell.getValue()}
          </DateComponent>
        ),
      }),
      columnHelper.accessor('duration', {
        id: 'duration',
        header: 'Duration',
      }),
      columnHelper.accessor('organizerName', {
        id: 'organizerName',
        enableHiding: false,
        header: 'Organizer',
      }),
      columnHelper.accessor('organizer', {
        id: 'organizer',
        header: 'Organizer',
        enableColumnFilter: false,
        cell: (cell) => {
          return <UserComponent property="organizer" user={cell.getValue()} />
        },
      }),
      columnHelper.accessor('numberOfParticipants', {
        id: 'participants',
        header: '# of Participants',
      }),
    ],
    [ActionButtons, ActionMenuItems, DateComponent, UserComponent, dateStartedFilterFn]
  )

  return (
    <DataGrid
      columns={columns}
      data={meetings}
      initialState={initialState}
      isError={isError}
      isFetching={isFetching}
      isPending={isPending}
      refetch={refetch}
    />
  )
}

const initialState: Partial<TableState> = {
  columnVisibility: { organizerName: false },
  sorting: [
    {
      id: 'startTime',
      desc: true,
    },
  ],
}

export type AugmentedMeetingDetails = Omit<MeetingDetailsWithoutChimeEvents, 'description'> & {
  description: string
  duration: string
  organizerName: string
  numberOfParticipants: number
}

const columnHelper = createColumnHelper<AugmentedMeetingDetails>()

function MeetingActionMenu({
  ActionButtons,
  ActionMenuItems,
  ...meetingDetails
}: AugmentedMeetingDetails & Pick<MeetingsTableProps, 'ActionButtons' | 'ActionMenuItems'>) {
  const { copyToClipboard } = useCopyToClipboard()

  const {
    isOpen: isOpenUpdateMeetingDrawer,
    onClose: onCloseUpdateMeetingDrawer,
    onOpen: onOpenUpdateMeetingDrawer,
  } = useDisclosure()

  return (
    <>
      <HStack>
        <Menu computePositionOnMount>
          <MenuButton
            aria-label="Open meeting menu"
            as={IconButton}
            icon={<MdMoreHoriz />}
            size="sm"
          />
          <Portal>
            <MenuList>
              <MenuItem onClick={() => copyToClipboard(meetingDetails.meetingId)}>
                Copy Inspection ID
              </MenuItem>
              <MenuItem onClick={onOpenUpdateMeetingDrawer}>Edit Inspection</MenuItem>
              {!!ActionMenuItems && <ActionMenuItems {...meetingDetails} />}
            </MenuList>
          </Portal>
        </Menu>

        {!!ActionButtons && <ActionButtons {...meetingDetails} />}
      </HStack>

      {isOpenUpdateMeetingDrawer && (
        <UpdateMeetingFormDrawer
          isOpen={isOpenUpdateMeetingDrawer}
          onClose={onCloseUpdateMeetingDrawer}
          {...meetingDetails}
        />
      )}
    </>
  )
}
